import { parseBoldEntries } from "./parse-entries";
import { asCost, asString, normalizeRisk, normalizeStringArray } from "./parse-properties";
import {
  extractBlockQuote,
  findH4Body,
  splitH3Entries,
  stripH4Subsections,
} from "./parse-sections";
import type { Component, Concern, Container, ContainerType } from "./types";

const CONTAINER_TYPES: ContainerType[] = [
  "frontend",
  "backend",
  "api",
  "database",
  "cache",
  "queue",
  "worker",
  "gateway",
  "storage",
  "cdn",
  "other",
];

function coerceContainerType(value: unknown): ContainerType {
  if (typeof value === "string") {
    const lower = value.toLowerCase() as ContainerType;
    if (CONTAINER_TYPES.includes(lower)) return lower;
  }
  return "other";
}

const CONCERN_TOP = /^-\s+(.+)$/;
const CONCERN_NESTED_PROP = /^\s{2,}-\s+([a-zA-Z_][a-zA-Z0-9_-]*)\s*:\s*(.+)$/;
const CONCERN_BOLD = /^\*\*([a-zA-Z0-9_\-./]+)\*\*\s*[:.\-—]?\s*(.*)$/;

function stripQuotedStr(value: string): string {
  let v = value.trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1);
  }
  return v;
}

function parseConcernsBody(body: string): Concern[] {
  const lines = body.split("\n");
  const out: Concern[] = [];
  let i = 0;
  let autoIdx = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const top = line.match(CONCERN_TOP);
    if (!top) {
      i += 1;
      continue;
    }
    const text = (top[1] ?? "").trim();

    const nested: Record<string, string> = {};
    let j = i + 1;
    while (j < lines.length) {
      const next = lines[j] ?? "";
      if (next.trim().length === 0) {
        j += 1;
        continue;
      }
      const propMatch = next.match(CONCERN_NESTED_PROP);
      if (!propMatch) break;
      const key = propMatch[1];
      const val = stripQuotedStr(propMatch[2] ?? "");
      if (key) nested[key] = val;
      j += 1;
    }

    const bold = text.match(CONCERN_BOLD);
    if (bold) {
      const id = (bold[1] ?? "").trim();
      const statement = stripQuotedStr(bold[2] ?? "");
      out.push({ id, statement, why: nested.why });
    } else {
      const statement = stripQuotedStr(text);
      if (statement.length > 0) {
        out.push({ id: `concern-${autoIdx}`, statement, why: nested.why });
        autoIdx += 1;
      }
    }
    i = j;
  }
  return out;
}

function parseTopLevelProperties(body: string): Record<string, unknown> {
  const lines = body.split("\n");
  const filtered: string[] = [];
  for (const line of lines) {
    if (/^####\s/.test(line)) break;
    if (/^>\s?/.test(line)) continue;
    filtered.push(line);
  }
  const props: Record<string, unknown> = {};
  let i = 0;
  while (i < filtered.length) {
    const line = filtered[i];
    if (line === undefined) {
      i += 1;
      continue;
    }
    const m = line.match(/^\s*-\s+([a-zA-Z_][a-zA-Z0-9_-]*)\s*:\s*(.*)$/);
    if (!m) {
      i += 1;
      continue;
    }
    const key = m[1];
    const value = (m[2] ?? "").trim();
    if (key === undefined) {
      i += 1;
      continue;
    }

    if (value.length === 0) {
      const items: unknown[] = [];
      let j = i + 1;
      while (j < filtered.length) {
        const next = filtered[j];
        if (next === undefined) break;
        const arr = next.match(/^\s{2,}-\s+(.+)$/);
        if (!arr) break;
        let item = (arr[1] ?? "").trim();
        if (item.startsWith('"') && item.endsWith('"')) item = item.slice(1, -1);
        items.push(item);
        j += 1;
      }
      props[key] = items;
      i = j;
      continue;
    }

    if (value.startsWith("[") && value.endsWith("]")) {
      const inner = value.slice(1, -1).trim();
      if (inner.length === 0) {
        props[key] = [];
      } else {
        props[key] = inner
          .split(",")
          .map((s) => s.trim().replace(/^"|"$/g, "").replace(/^'|'$/g, ""))
          .filter((s) => s.length > 0);
      }
      i += 1;
      continue;
    }

    let scalar: unknown = value;
    if (value === "true") scalar = true;
    else if (value === "false") scalar = false;
    else if (/^-?\d+$/.test(value)) scalar = Number(value);
    else if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      scalar = value.slice(1, -1);
    }
    props[key] = scalar;
    i += 1;
  }
  return props;
}

function parseComponentsBody(body: string, containerId: string): Component[] {
  const entries = parseBoldEntries(body);
  return entries.map((entry) => {
    const props = entry.properties;
    const {
      label,
      headline,
      description,
      why,
      technology,
      connects_to,
      risk,
      cost,
      group,
      ...rest
    } = props;
    return {
      id: entry.id,
      containerId,
      label: asString(label) ?? entry.id,
      headline: asString(headline),
      description: asString(description),
      why: asString(why),
      technology: normalizeStringArray(technology),
      connects_to: normalizeStringArray(connects_to),
      risk: normalizeRisk(risk),
      cost: asCost(cost),
      group: asString(group),
      ...rest,
    };
  });
}

export function parseContainers(sectionBody: string): Container[] {
  const entries = splitH3Entries(sectionBody);
  return entries.map((entry) => {
    const id = entry.title.trim();
    const body = entry.body;

    const topProps = parseTopLevelProperties(body);
    const narrative = extractBlockQuote(stripH4Subsections(body));
    const concernsBody = findH4Body(body, "Concerns") ?? "";
    const concerns = parseConcernsBody(concernsBody);
    const componentsBody = findH4Body(body, "Components") ?? "";
    const components = parseComponentsBody(componentsBody, id);

    const {
      label,
      headline,
      description,
      why,
      technology,
      type,
      repo_path,
      deploys_to,
      actors,
      connects_to,
      cost,
      risk,
      tags,
      ...rest
    } = topProps;

    return {
      id,
      label: asString(label) ?? id,
      headline: asString(headline),
      description: asString(description),
      why: asString(why),
      technology: normalizeStringArray(technology),
      type: coerceContainerType(type),
      repo_path: asString(repo_path),
      deploys_to: normalizeStringArray(deploys_to),
      actors: normalizeStringArray(actors),
      connects_to: normalizeStringArray(connects_to),
      cost: asCost(cost),
      risk: normalizeRisk(risk),
      tags: normalizeStringArray(tags),
      narrative,
      concerns,
      components,
      ...rest,
    };
  });
}
